import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ScrollToTop from "@/components/base/ScrollToTop";
import SEOHead from "@/components/base/SEOHead";
import Breadcrumb from "@/components/base/Breadcrumb";

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于91精选 - 精品内容聚合平台",
  description: "了解91精选的品牌故事、核心价值和团队理念。91精选是专注优质内容发现与聚合的精品平台。",
  url: "https://91jingxuan.com/about",
};

const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "91精选",
  url: "https://91jingxuan.com",
  description: "精品内容聚合平台，专注优质内容发现与精选推荐",
  foundingDate: "2024",
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer service",
    email: "contact@91jingxuan.com",
    availableLanguage: ["Chinese"],
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "91精选的内容来源是什么？",
      acceptedAnswer: {
        "@type": "Answer",
        text: "91精选的内容由专业编辑团队从海量资源中精心筛选和整理。我们严格把控内容质量，确保每一篇推荐都经过实测验证，为用户提供真正有价值的精选内容。",
      },
    },
    {
      "@type": "Question",
      name: "91精选的内容多久更新一次？",
      acceptedAnswer: {
        "@type": "Answer",
        text: "91精选保持每日更新的节奏，每周推送不少于5篇精选内容。我们的编辑团队持续关注各领域的最新动态，确保用户能第一时间获取优质资源。",
      },
    },
    {
      "@type": "Question",
      name: "如何向91精选推荐内容？",
      acceptedAnswer: {
        "@type": "Answer",
        text: "如果您有优质资源想推荐给91精选，可以通过联系我们页面的表单提交推荐信息，我们的编辑团队会认真评估每一份推荐。",
      },
    },
    {
      "@type": "Question",
      name: "91精选的内容可以转载吗？",
      acceptedAnswer: {
        "@type": "Answer",
        text: "91精选的内容均受版权保护。如需转载，请通过联系我们页面获取授权。我们欢迎合理的引用和分享，但请务必注明出处并保留原文链接。",
      },
    },
    {
      "@type": "Question",
      name: "如何订阅91精选的内容推送？",
      acceptedAnswer: {
        "@type": "Answer",
        text: "您可以在91精选首页底部输入邮箱地址订阅我们的精选推送，我们会定期将优质内容直接发送到您的邮箱，帮助您不错过任何精彩。",
      },
    },
  ],
};

const testimonials = [
  {
    name: "张先生",
    role: "产品经理",
    content: "91精选帮我节省了大量筛选信息的时间，每周的推送内容质量都很高，已经推荐给了整个团队。",
    avatar: "https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20Asian%20man%20in%20clean%20white%20shirt%2C%20warm%20studio%20lighting%2C%20friendly%20expression%2C%20neutral%20gray%20background%2C%20professional%20corporate%20style&width=100&height=100&seq=avatar-01&orientation=squarish",
  },
  {
    name: "李女士",
    role: "自由设计师",
    content: "设计素材分类简直就是宝藏！在这里找到的免费可商用资源比很多付费网站的还好用，爱了爱了。",
    avatar: "https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20Asian%20woman%20with%20warm%20smile%2C%20modern%20creative%20style%2C%20soft%20studio%20lighting%2C%20neutral%20background%2C%20professional%20yet%20approachable&width=100&height=100&seq=avatar-02&orientation=squarish",
  },
  {
    name: "王同学",
    role: "计算机专业研究生",
    content: "技术文章分类的内容深度很到位，每篇都值得精读。对于想跟上技术前沿的我来说，91精选是必备的信息源。",
    avatar: "https://readdy.ai/api/search-image?query=Professional%20headshot%20portrait%20of%20young%20Asian%20student%20in%20casual%20shirt%2C%20bright%20friendly%20expression%2C%20modern%20studio%20lighting%2C%20clean%20white%20background&width=100&height=100&seq=avatar-03&orientation=squarish",
  },
];

const teamStats = [
  { label: "精选文章", value: "2,000+" },
  { label: "内容分类", value: "6个" },
  { label: "月度读者", value: "50万+" },
  { label: "用户好评率", value: "98.5%" },
];

export default function AboutPage() {
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [contactStatus, setContactStatus] = useState<"idle" | "success" | "error">("idle");
  const [contactError, setContactError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    setContactError("");

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get("phone_alt")?.toString().trim();
    if (honeypot) {
      setContactStatus("success");
      setContactForm({ name: "", email: "", subject: "", message: "" });
      setSubmitting(false);
      setTimeout(() => setContactStatus("idle"), 3000);
      return;
    }

    formData.delete("phone_alt");

    try {
      const response = await fetch(
        "https://readdy.ai/api/form/d9pkcq5i2vhsdh2d368g",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: new URLSearchParams(
            Object.fromEntries(formData.entries()) as Record<string, string>
          ).toString(),
        }
      );

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === "OK") {
        setContactStatus("success");
        setContactForm({ name: "", email: "", subject: "", message: "" });
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setContactError(serverMsg || "提交失败，请稍后重试");
        setContactStatus("error");
      }
    } catch {
      setContactError("网络错误，请检查连接后重试");
      setContactStatus("error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于91精选 - 品牌介绍与联系方式"
        description="了解91精选的品牌故事、核心价值和团队理念。91精选是专注优质内容发现与聚合的精品平台，严格筛选每一篇内容，为用户节省时间。查看用户口碑与联系我们。"
        keywords="关于91精选,91精选品牌介绍,91精选联系方式,91精选口碑,精选内容平台介绍"
        canonicalUrl="/about"
        schema={{ ...aboutSchema, ...orgSchema, ...faqSchema }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24 pb-20">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="max-w-[900px] mx-auto">
              <Breadcrumb
                items={[
                  { label: "首页", href: "/" },
                  { label: "关于我们" },
                ]}
              />

              {/* Brand Introduction */}
              <section className="mt-8 mb-20">
                <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 mb-6">
                  关于91精选
                </h1>
                <div className="prose prose-lg max-w-none text-foreground-700 leading-relaxed">
                  <p className="text-lg">
                    <strong>91精选</strong>是一个专注优质内容发现与聚合的精品平台。在信息爆炸的时代，我们深知找到真正有价值的内容变得越来越困难。因此，91精选应运而生——我们致力于成为您最信赖的内容筛选伙伴。
                  </p>
                  <p>
                    每一篇出现在91精选上的内容，都经过了编辑团队的严格筛选和多轮评估。我们不追求数量，只在乎质量。无论是实用工具、设计素材、技术文章还是学习资源，我们都确保推荐的内容经得起时间的检验。
                  </p>
                  <h2 className="text-2xl font-bold text-foreground-900 mt-10 mb-4">我们的使命</h2>
                  <p>
                    帮助用户在海量信息中快速发现真正值得收藏的优质内容，节省筛选时间，提升信息获取效率。我们相信，好的内容值得被更多人看到，而好的筛选机制能让这个过程变得更加高效和愉悦。
                  </p>
                  <h2 className="text-2xl font-bold text-foreground-900 mt-10 mb-4">核心价值</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
                    <div className="bg-background-100 rounded-lg p-6">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-4">
                        <i className="ri-shield-check-line text-lg"></i>
                      </div>
                      <h4 className="font-semibold text-foreground-900 mb-2">严格筛选</h4>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        每篇内容经过多轮评估，确保推荐的都是真实好用的优质资源
                      </p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-6">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-4">
                        <i className="ri-time-line text-lg"></i>
                      </div>
                      <h4 className="font-semibold text-foreground-900 mb-2">节省时间</h4>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        帮您从海量信息中快速定位精华内容，把时间留给真正重要的事
                      </p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-6">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-4">
                        <i className="ri-sparkling-line text-lg"></i>
                      </div>
                      <h4 className="font-semibold text-foreground-900 mb-2">持续更新</h4>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        每日更新优质内容，紧跟各领域最新动态，让您始终走在信息前沿
                      </p>
                    </div>
                    <div className="bg-background-100 rounded-lg p-6">
                      <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-700 mb-4">
                        <i className="ri-heart-line text-lg"></i>
                      </div>
                      <h4 className="font-semibold text-foreground-900 mb-2">用户至上</h4>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        以用户需求为核心，持续优化内容质量和阅读体验
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Stats */}
              <section className="mb-20 bg-background-100 rounded-lg py-12 px-6">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                  {teamStats.map((stat) => (
                    <div key={stat.label} className="text-center">
                      <div className="text-3xl md:text-4xl font-bold text-primary-500 mb-2">
                        {stat.value}
                      </div>
                      <div className="text-sm text-foreground-600">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </section>

              {/* Reputation */}
              <section id="reputation" className="mb-20">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3 text-center">
                  用户口碑
                </h2>
                <p className="text-foreground-600 text-sm text-center mb-10">
                  来自真实用户的评价，是我们前进的最大动力
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {testimonials.map((item) => (
                    <div
                      key={item.name}
                      className="bg-background-100 rounded-lg p-6"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <img
                          src={item.avatar}
                          alt={item.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        <div>
                          <div className="text-sm font-semibold text-foreground-900">
                            {item.name}
                          </div>
                          <div className="text-xs text-foreground-500">{item.role}</div>
                        </div>
                      </div>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        &ldquo;{item.content}&rdquo;
                      </p>
                    </div>
                  ))}
                </div>
              </section>

              {/* FAQ */}
              <section className="mb-20">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3 text-center">
                  常见问题
                </h2>
                <p className="text-foreground-600 text-sm text-center mb-10">
                  您可能想了解的信息
                </p>
                <div className="space-y-4 max-w-[700px] mx-auto">
                  {faqSchema.mainEntity.map((faq, index) => (
                    <details
                      key={index}
                      className="group bg-background-100 rounded-lg overflow-hidden"
                    >
                      <summary className="flex items-center justify-between px-6 py-4 cursor-pointer text-sm font-medium text-foreground-900 hover:text-primary-500 transition-colors duration-200">
                        {faq.name}
                        <i className="ri-arrow-down-s-line text-lg transition-transform duration-200 group-open:rotate-180"></i>
                      </summary>
                      <div className="px-6 pb-4 text-sm text-foreground-600 leading-relaxed">
                        {faq.acceptedAnswer.text}
                      </div>
                    </details>
                  ))}
                </div>
              </section>

              {/* Contact */}
              <section id="contact" className="mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-3 text-center">
                  联系我们
                </h2>
                <p className="text-foreground-600 text-sm text-center mb-10">
                  有任何问题或建议？请随时与我们联系
                </p>
                <div className="max-w-[600px] mx-auto bg-background-100 rounded-lg p-8">
                  <form
                    onSubmit={handleContactSubmit}
                    data-readdy-form
                    className="space-y-5"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label
                          htmlFor="contact-name"
                          className="block text-sm font-medium text-foreground-800 mb-1.5"
                        >
                          姓名
                        </label>
                        <input
                          type="text"
                          id="contact-name"
                          name="name"
                          value={contactForm.name}
                          onChange={(e) =>
                            setContactForm({ ...contactForm, name: e.target.value })
                          }
                          required
                          className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
                          placeholder="您的姓名"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="contact-email"
                          className="block text-sm font-medium text-foreground-800 mb-1.5"
                        >
                          邮箱
                        </label>
                        <input
                          type="email"
                          id="contact-email"
                          name="email"
                          value={contactForm.email}
                          onChange={(e) =>
                            setContactForm({ ...contactForm, email: e.target.value })
                          }
                          required
                          className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
                          placeholder="your@email.com"
                        />
                      </div>
                    </div>
                    <div>
                      <label
                        htmlFor="contact-subject"
                        className="block text-sm font-medium text-foreground-800 mb-1.5"
                      >
                        主题
                      </label>
                      <input
                        type="text"
                        id="contact-subject"
                        name="subject"
                        value={contactForm.subject}
                        onChange={(e) =>
                          setContactForm({ ...contactForm, subject: e.target.value })
                        }
                        required
                        className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
                        placeholder="请简要描述您的需求"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="contact-message"
                        className="block text-sm font-medium text-foreground-800 mb-1.5"
                      >
                        留言内容
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        value={contactForm.message}
                        onChange={(e) =>
                          setContactForm({ ...contactForm, message: e.target.value })
                        }
                        required
                        maxLength={500}
                        rows={5}
                        className="w-full px-4 py-2.5 rounded-lg text-sm bg-background-50 border border-background-200/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      />
                      <div className="text-xs text-foreground-400 mt-1 text-right">
                        {contactForm.message.length}/500
                      </div>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="website-alt-field"
                    />
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-all duration-300 cursor-pointer disabled:opacity-60 whitespace-nowrap"
                    >
                      {submitting ? (
                        <>
                          <i className="ri-loader-4-line mr-1.5 animate-spin"></i>
                          提交中...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line mr-1.5"></i>
                          发送消息
                        </>
                      )}
                    </button>
                    {contactStatus === "success" && (
                      <p className="text-sm text-green-600 text-center">
                        消息已成功发送！我们会尽快回复您。
                      </p>
                    )}
                    {contactStatus === "error" && contactError && (
                      <p className="text-sm text-red-500 text-center">{contactError}</p>
                    )}
                  </form>
                </div>
              </section>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}